import { DigitInput } from 'components/shared/digit-input';
import { useTranslateForm } from 'hooks/use-translate-payoff';
import { NumberFieldMetadata, PercentFieldMetadata } from 'models/forms/fields';
import { useCallback } from 'react';
import { ErrorWrapper } from 'routes/essential-workspace/components/form/error-wrapper';
import { useFormErrors } from 'routes/essential-workspace/components/form/use-form-errors';
import { useFormFieldData } from 'routes/essential-workspace/components/form/use-form-field-data';

type DigitFieldProps = PercentFieldMetadata | NumberFieldMetadata;

export const DEFAULT_INCREMENT_STEP = 1;
export function DigitField({ id, translationKey, unit, nbDecimals, decimalPadding, isReadOnly }: DigitFieldProps) {
  const [value, setFormData] = useFormFieldData<Nullable<number>>(id);

  const { getFieldError } = useFormErrors();
  const fieldError = getFieldError(id);

  const { translateLabel } = useTranslateForm();
  const label = translateLabel(translationKey ?? id);

  const handleOnValueChanged = useCallback(
    (newValue: Nullable<number>) => {
      if (value !== newValue) {
        setFormData(newValue);
      }
    },
    [setFormData, value],
  );

  const handleAddOrRemove = useCallback(
    (action: 'add' | 'remove') => {
      const newValue = action === 'add' ? (value ?? 0) + DEFAULT_INCREMENT_STEP : (value ?? 0) - DEFAULT_INCREMENT_STEP;
      setFormData(newValue);
    },
    [value, setFormData],
  );

  return (
    <ErrorWrapper error={fieldError}>
      <DigitInput
        id={id}
        label={label}
        unit={unit}
        value={value}
        precision={nbDecimals}
        onValueChanged={handleOnValueChanged}
        onAddOrRemove={handleAddOrRemove}
        decimalPadding={decimalPadding}
        isReadOnly={isReadOnly}
        incrementStep={DEFAULT_INCREMENT_STEP}
        isInvalid={fieldError?.severity === 'Error'}
        highlightColor={fieldError?.severity === 'Warning' ? 'info' : undefined}
      />
    </ErrorWrapper>
  );
}
